import React, { useState, useEffect } from "react";
import {
  X,
  CheckCircle,
  Wallet,
  CreditCard,
  QrCode,
  Banknote,
  Printer,
  ArrowLeft,
  ShoppingCart,
  FileText,
  Tag,
  Edit2,
  ArrowLeftRight,
  ShoppingBasket,
  Layers,
  User,
  MessageCircle,
  ShoppingBag,
  Smartphone,
  Utensils,
} from "lucide-react";
import {
  createSalesOrder,
  addOrderPayment,
  updateSalesOrderStatus,
  getSalesOrderDetail,
  updateSalesOrder,
} from "../../api/salesService";
import { accountService } from "../../api/accountService";
import { configService } from "../../api/configService";
import { masterService } from "../../api/masterService";
import SearchableSelect from "../SearchableSelect";
import { calculateOrderItemTotals, calculateOrderTotals } from "../../utils/salesUtils";
import { getSellingUnitPayload } from "../../utils/orderItemUnits";
import { buildReceiptHtml, buildOrderReceiptHtml, getReceiptConfig, filterLabelItems, detectVehicleUnit } from "../../utils/receiptConfig";
import { executePrint, executePrintBoth, executePrintLabel } from "../../utils/printManager";
import EditBillingModal from "./EditBillingModal";
import ItemAdjustmentModal from "./ItemAdjustmentModal";
import { useNotification } from "../../contexts/NotificationContext";

// Helper components for the new layout

const TransactionDetails = ({ cart, onClose, onUpdateItemAdjustment }) => {
  const [adjustmentTarget, setAdjustmentTarget] = useState(null);

  return (
  <div className="w-full lg:flex-1 lg:flex-shrink-0 p-6 flex flex-col lg:h-full lg:min-h-0">
    <h3 className="font-bold text-lg mb-1">Detail Transaksi</h3>
    <p className="text-sm text-gray-500 mb-4">
      Detail informasi produk yang akan dibayar
    </p>
    <div className="border-t border-gray-200 mb-4" />
    <div className="space-y-3 flex-grow overflow-y-auto pr-1 pb-4">
      {cart
        .map((item, originalIndex) => ({ item, originalIndex }))
        .sort((a, b) =>
          (a.item.item_name || a.item.name || a.item.product_name || "").localeCompare(
            b.item.item_name || b.item.name || b.item.product_name || "",
          ),
        )
        .map(({ item, originalIndex }, i) => (
          <div
            key={item.id || item.product_id || i}
            className="bg-white rounded-lg p-3.5 border border-gray-200">
            <div className="flex justify-between items-start mb-0.5">
              <div className="flex-1 pr-2 min-w-0">
                <p className="font-bold text-gray-800 text-sm leading-snug">
                  {(() => {
                    const nameStr = item.product_name || item.name || item.item_name || "";
                    const suffix = item.variant_name ? ` - ${item.variant_name}` : "";
                    return (suffix && nameStr.endsWith(suffix)) ? nameStr.slice(0, -suffix.length) : nameStr;
                  })()}
                </p>
                {item.variant_name && (
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Varian: <span className="font-medium text-gray-700">{item.variant_name}</span>
                  </p>
                )}
                {item.selected_options && item.selected_options.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {item.selected_options.map((opt, oIdx) => (
                        <span
                          key={oIdx}
                          className="inline-flex items-center text-[10px] bg-slate-100 text-slate-700 font-medium px-2 py-0.5 rounded-md">
                          + {opt.name} {opt.qty > 1 ? `x${opt.qty}` : ''} (Rp {(opt.price || opt.sell_price || 0).toLocaleString()})
                        </span>
                    ))}
                  </div>
                )}
                {(item.unit_name || item.unit?.name) && (
                  <p className="text-[11px] text-teal-700 mt-1 inline-flex items-center gap-1 bg-teal-50 ring-1 ring-teal-200 px-1.5 py-0.5 rounded font-medium">
                    Unit: {item.unit_name || item.unit?.name}
                  </p>
                )}
              </div>
              <p className="text-xs font-bold text-gray-800 whitespace-nowrap ml-2">
                {item.quantity || item.qty} {(item.satuan || "PCS").toUpperCase()}
              </p>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Rp {(item.unit_price || item.price || 0).toLocaleString("id-ID")}
            </p>
            {Number(item.discount_amount || 0) > 0 && (
              <div className="flex justify-between text-xs text-rose-600">
                <span>Diskon Item</span>
                <span>- Rp {Number(item.discount_amount).toLocaleString("id-ID")}</span>
              </div>
            )}
            {Number(item.additional_cost || 0) > 0 && (
              <div className="flex justify-between text-xs text-blue-600">
                <span>Biaya Tambahan Item</span>
                <span>+ Rp {Number(item.additional_cost).toLocaleString("id-ID")}</span>
              </div>
            )}
            <div className="flex justify-between items-center pt-2 border-t border-dashed border-gray-200">
              <p className="text-xs text-gray-500">Total:</p>
              <p className="text-sm font-bold text-gray-900">
                Rp{" "}
                {calculateOrderItemTotals(item).subtotal.toLocaleString("id-ID")}
              </p>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setAdjustmentTarget({ item, originalIndex, field: "discount" })}
                className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-100">
                <Tag className="h-3.5 w-3.5" />
                {Number(item.discount_amount || 0) > 0 ? "Ubah Diskon" : "Diskon"}
              </button>
              <button
                type="button"
                onClick={() => setAdjustmentTarget({ item, originalIndex, field: "additionalCost" })}
                className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-100">
                <Edit2 className="h-3.5 w-3.5" />
                {Number(item.additional_cost || 0) > 0 ? "Ubah Biaya Tambahan" : "Biaya Tambahan"}
              </button>
            </div>
            {item.notes && (
              <p className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-200 italic">
                {item.notes}
              </p>
            )}
          </div>
        ))}
    </div>
    <div className="mt-2 pt-4 border-t border-gray-200 hidden lg:block">
      <button
        onClick={onClose}
        className="w-auto px-4 py-2 flex items-center justify-center gap-2 bg-red-50 text-red-600 rounded-md font-semibold text-sm hover:bg-red-100 transition-colors">
        <ArrowLeft className="w-4 h-4" />
        Kembali Ke Halaman Kasir
      </button>
    </div>
    {adjustmentTarget && (
      <ItemAdjustmentModal
        field={adjustmentTarget.field}
        item={adjustmentTarget.item}
        onClose={() => setAdjustmentTarget(null)}
        onSave={(adjustment) =>
          onUpdateItemAdjustment(adjustmentTarget.originalIndex, adjustment)
        }
      />
    )}
  </div>
  );
};

const BillingSummary = ({
  totals,
  discountPercentage = 0,
  onUpdateField,
  notes,
  onNotesChange,
  isDownPayment,
  setIsDownPayment,
  paymentMethod,
  sellerType,
  isManualDiscount,
  isManualMarkup,
  customer,
  walkInName,
  selectedTable,
  pendingOrder,
}) => {
  const isSaleTypeDiscount = (sellerType?.jenis === 'Diskon') && !isManualDiscount;
  const isSaleTypeMarkup = (sellerType?.jenis === 'Markup') && !isManualMarkup;

  const displayAdditionalCosts = isSaleTypeMarkup ? 0 : (totals.additionalCosts || 0);
  const displayDiscountTotal = isSaleTypeDiscount ? 0 : (totals.discountTotal || 0);
  const displayDiscountPercentage = isSaleTypeDiscount ? 0 : discountPercentage;

  return (
    <div className="w-full lg:flex-1 p-6 border-y lg:border-y-0 lg:border-l lg:border-r border-gray-200 flex flex-col lg:h-full lg:min-h-0 lg:overflow-y-auto">
      <h3 className="font-bold text-lg mb-1">Total Tagihan</h3>
      <p className="text-sm text-gray-500 mb-4">
        Tambahkan biaya dan catatan apabila diperlukan
      </p>

      {/* Info Pelanggan & Meja */}
      <div className="bg-teal-50/60 border border-teal-100/80 rounded-xl p-3 mb-4 space-y-1.5">
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-500 font-medium">Pelanggan:</span>
          <span className="font-bold text-slate-800">{customer?.name || (walkInName && walkInName.trim()) || "Pelanggan Umum"}</span>
        </div>
        {localStorage.getItem("PREVIEW_APPS") === "true" && (selectedTable?.name || pendingOrder?.table_name) && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500 font-medium">Meja:</span>
            <span className="font-bold text-teal-700">{selectedTable?.name || pendingOrder?.table_name}</span>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 mb-4" />

      <div className="space-y-6">
        {/* Subtotal */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-gray-600">
            <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center">
              <ShoppingCart className="w-4 h-4 text-teal-500" />
            </div>
            <span className="text-sm">Subtotal</span>
          </div>
          <span className="font-bold text-gray-800">
            Rp {totals.subtotal.toLocaleString("id-ID")}
          </span>
        </div>

        {/* Biaya Tambahan */}
        {/* [HIDDEN_SALE_TYPE_MARKUP]: Info markup dari tipe penjualan disembunyikan (tampil Rp 0), tapi row & tombol edit tetap muncul */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-gray-600">
            <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center">
              <FileText className="w-4 h-4 text-teal-500" />
            </div>
            <span className="text-sm">Biaya Tambahan</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onUpdateField("additionalCosts")}
              className="text-teal-500 hover:text-teal-700 p-0.5">
              <Edit2 className="w-3.5 h-3.5" />
            </button>
            <span className="font-bold text-gray-800">
              Rp {displayAdditionalCosts.toLocaleString("id-ID")}
            </span>
          </div>
        </div>

        {/* Diskon Total */}
        {/* [HIDDEN_SALE_TYPE_DISCOUNT]: Info diskon dari tipe penjualan disembunyikan (tampil 0% & Rp 0), tapi row & tombol edit tetap muncul */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-gray-600">
            <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center">
              <Tag className="w-4 h-4 text-teal-500" />
            </div>
            <span className="text-sm">Diskon Total ({displayDiscountPercentage}%)</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onUpdateField("discount")}
              className="text-orange-500 hover:text-orange-700 p-0.5">
              <Edit2 className="w-3.5 h-3.5" />
            </button>
            <span className="font-bold text-orange-500">
              - Rp {displayDiscountTotal.toLocaleString("id-ID")}
            </span>
          </div>
        </div>
      
      {/* Grand Total */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center">
            <Printer className="w-4 h-4 text-teal-500" />
          </div>
          <span className="text-sm text-gray-600">Grand Total</span>
        </div>
        <span className="font-bold text-gray-900">
          Rp {totals.grandTotal.toLocaleString("id-ID")}
        </span>
      </div>
    </div>

    {totals.dpAmount === 0 && paymentMethod !== "MIXED" && (
      <label className="mt-4 flex items-start gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50 cursor-pointer hover:bg-amber-100/70 transition-colors">
        <input
          type="checkbox"
          checked={isDownPayment}
          onChange={(e) => setIsDownPayment(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded text-amber-600 focus:ring-amber-500 accent-amber-600"
        />
        <div className="flex-1">
          <p className="text-sm font-semibold text-amber-800">
            Pembayaran DP / Uang Muka
          </p>
          <p className="text-xs text-amber-700/90 mt-0.5">
            Bayar sebagian dahulu. Transaksi akan masuk ke{" "}
            <span className="font-semibold">Transaksi Pending</span> sampai
            pelunasan.
          </p>
        </div>
      </label>
    )}

    {totals.dpAmount > 0 && (
      <div className="mt-4 border-t border-gray-200 pt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center">
              <Banknote className="w-4 h-4 text-amber-500" />
            </div>
            <span className="text-sm text-amber-600 font-semibold">DP</span>
          </div>
          <span className="font-bold text-amber-600">
            - Rp {totals.dpAmount.toLocaleString("id-ID")}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-teal-700">Sisa Tagihan</span>
          </div>
          <span className="font-bold text-lg text-teal-700">
            Rp {Math.max(0, totals.grandTotal - totals.dpAmount).toLocaleString("id-ID")}
          </span>
        </div>
      </div>
    )}

    {/* Notes */}
    <div className="mt-auto pt-8">
      <h4 className="font-bold text-gray-800 mb-2">Tambah Catatan</h4>
      <textarea
        placeholder="Tambahkan catatan disini apabila diperlukan..."
        rows="4"
        value={notes}
        onChange={onNotesChange}
        className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
      />
    </div>
  </div>
  );
};

const PAYMENT_METHODS = [
  { key: "CASH", label: "Bayar Tunai", icon: ShoppingBasket },
  { key: "TRANSFER", label: "Transfer", icon: ArrowLeftRight },
  { key: "QRIS", label: "QRIS", icon: QrCode },
  { key: "MIXED", label: "Campuran", icon: Layers },
];

const getCashRecommendations = (amount) => {
  if (amount <= 0) return [50000, 100000, 150000, 200000];
  const recommendations = new Set();
  
  const ceilTo = (val, step) => Math.ceil(val / step) * step;
  
  [1000, 2000, 5000, 10000, 20000, 50000, 100000].forEach(step => {
    recommendations.add(ceilTo(amount, step));
  });
  
  let result = Array.from(recommendations)
    .filter(val => val > amount)
    .sort((a, b) => a - b);
    
  return result.slice(0, 4);
};

const isAccountSupportedForMethod = (account, method) => {
  if (!account) return false;
  // Laci Kasir adalah khusus Tunai (Cash), tidak boleh muncul di metode Transfer, QRIS, atau Campuran
  if (account.name?.toLowerCase() === "laci kasir") {
    return false;
  }

  const rawMethods = account.payment_methods || account.description || "";
  if (!rawMethods || String(rawMethods).trim() === "") {
    return true;
  }
  const methods = String(rawMethods)
    .toUpperCase()
    .split(",")
    .map((m) => m.trim());

  if (method === "TRANSFER") {
    return methods.includes("TRANSFER");
  }
  if (method === "QRIS") {
    return methods.includes("QRIS");
  }
  if (method === "MIXED") {
    return methods.includes("CAMPURAN");
  }
  return true;
};

const PaymentMethod = React.forwardRef(({
  grandTotal,
  onProcess,
  isProcessing,
  onClose,
  paymentAccounts = [],
  loadingAccounts = false,
  dpAmount = 0,
  paymentMethod,
  setPaymentMethod,
  isDownPayment,
  setIsDownPayment,
}, ref) => {
  const [cashAmount, setCashAmount] = useState("");
  const [accountId, setAccountId] = useState("");
  const [proofFile, setProofFile] = useState(null);

  useEffect(() => {
    if (isDownPayment) {
      setCashAmount("");
    }
  }, [isDownPayment]);

  const filteredPaymentAccounts = paymentAccounts.filter((account) =>
    isAccountSupportedForMethod(account, paymentMethod)
  );

  useEffect(() => {
    const isCurrentValid = filteredPaymentAccounts.some((acc) => acc.id === accountId);
    if (!isCurrentValid) {
      setAccountId(filteredPaymentAccounts.length > 0 ? filteredPaymentAccounts[0].id : "");
    }
  }, [paymentMethod, filteredPaymentAccounts]);

  const handleNumericInput = (setter) => (e) => {
    const value = e.target.value.replace(/\./g, "");
    if (value === "" || /^\d+$/.test(value)) {
      setter(value ? parseInt(value, 10).toLocaleString("id-ID") : "");
    }
  };

  const cashValue = parseFloat(cashAmount.replace(/\./g, "")) || 0;
  const isCash = paymentMethod === "CASH";
  const isMixed = paymentMethod === "MIXED";
  const isNonCash = !isCash;
  const requiresAccount = !isCash;
  const remainingToPay = Math.max(0, grandTotal - dpAmount);
  const mixedElectronicAmount = Math.max(0, remainingToPay - cashValue);
  const paymentAccountOptions = filteredPaymentAccounts.map((account) => ({
    id: account.id,
    name: account.name,
  }));
  // When DP mode is active for non-cash methods, the entered cash amount
  // represents the partial nominal being paid (DP) instead of the full
  // electronic amount.
  const totalPaid = isCash
    ? cashValue
    : isMixed
      ? remainingToPay
      : isDownPayment
        ? cashValue
        : remainingToPay;
  const change = isCash && !isDownPayment ? totalPaid - remainingToPay : 0;
  const isSufficient = isDownPayment
    ? cashValue > 0 && cashValue < remainingToPay
    : isCash
      ? cashValue >= remainingToPay
      : isMixed
        ? cashValue > 0 && cashValue < remainingToPay && accountId
        : accountId;
  const hasRequiredAmount = isDownPayment
    ? cashAmount !== ""
    : isCash
      ? cashAmount !== ""
      : isMixed
        ? cashAmount !== ""
        : true;

  React.useImperativeHandle(ref, () => ({
    submit: () => {
      if (isProcessing) return;
      if (!isSufficient || !hasRequiredAmount || (requiresAccount && !accountId)) return;
      onProcess({
        paymentMethod,
        cashAmount: isCash ? cashValue : isMixed ? cashValue : 0,
        electronicAmount: isCash
          ? 0
          : isMixed
            ? mixedElectronicAmount
            : isDownPayment
              ? cashValue
              : grandTotal,
        totalPaid,
        accountId: requiresAccount ? accountId : null,
        proofFile,
        isDownPayment,
      });
    }
  }));

  return (
    <div className="w-full lg:flex-1 lg:flex-shrink-0 p-6 flex flex-col rounded-b-2xl lg:rounded-r-2xl lg:min-h-0">
      <div className="flex justify-between items-start mb-1">
        <div>
          <h3 className="font-bold text-lg">Metode Pembayaran</h3>
          <p className="text-sm text-gray-500">
            Pilih jenis pembayaran yang akan digunakan
          </p>
        </div>
        {/* Close button moved to left panel */}
      </div>

      <div className="border-t border-gray-200 my-4" />

      <div className="flex-1 lg:overflow-y-auto pr-1 pb-2 min-h-0 flex flex-col space-y-4">

      <div className="flex justify-between items-center text-xl font-bold border-t border-gray-200 pt-2">
        <span className="text-gray-900">{dpAmount > 0 ? "Sisa Tagihan" : "Total Tagihan"}</span>
        <span className="text-teal-600">
          Rp {remainingToPay.toLocaleString("id-ID")}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-4">
        {PAYMENT_METHODS.map(({ key, label, icon: Icon }) => {
          const isActive = paymentMethod === key;
          return (
            <button
              key={key}
              onClick={() => setPaymentMethod(key)}
              className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-semibold border transition-all ${isActive
                  ? "bg-teal-600 text-white border-teal-600"
                  : "bg-white text-teal-600 border-gray-300 hover:border-teal-400"
                }`}>
              <Icon className="w-4 h-4 flex-shrink-0" />
              {label}
            </button>
          );
        })}
      </div>



      <div className="mt-5 space-y-3">
        {(isCash || isMixed || isDownPayment) && (
          <div>
            <label className="text-sm font-semibold text-gray-700 block mb-1.5">
              {isDownPayment
                ? "Nominal DP / Uang Muka"
                : isMixed
                  ? "Nominal Cash (Porsi Tunai)"
                  : "Jumlah Dibayar"}
            </label>
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-teal-500 focus-within:border-teal-500">
              <span className="px-3 py-3 text-gray-500 text-sm bg-gray-50 border-r border-gray-300 font-medium">
                Rp.
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={cashAmount}
                onChange={handleNumericInput(setCashAmount)}
                placeholder="0"
                className="flex-1 px-3 py-3 text-right font-semibold text-gray-800 focus:outline-none bg-white"
              />
            </div>
            {isDownPayment && cashValue > 0 && cashValue < grandTotal && (
              <p className="text-xs text-amber-700 mt-1.5">
                Sisa tagihan:{" "}
                <span className="font-semibold">
                  Rp {(grandTotal - cashValue).toLocaleString("id-ID")}
                </span>
              </p>
            )}
            {isDownPayment && cashValue >= grandTotal && cashValue > 0 && (
              <p className="text-xs text-red-600 mt-1.5">
                Nominal DP harus lebih kecil dari total tagihan.
              </p>
            )}
          </div>
        )}

        {isCash && !isDownPayment && (
          <div className="mt-3">
            <div className="grid grid-cols-2 gap-2 mb-2">
              {getCashRecommendations(remainingToPay).map((recom, idx) => {
                const isSelected = cashValue === recom;
                return (
                  <button
                    key={idx}
                    onClick={() => setCashAmount(recom.toLocaleString("id-ID"))}
                    className={`py-2 border rounded-lg font-bold text-sm transition-colors ${
                      isSelected
                        ? "bg-teal-600 text-white border-teal-600"
                        : "border-gray-300 text-gray-700 hover:border-teal-500 hover:text-teal-600 bg-white"
                    }`}>
                    Rp {recom.toLocaleString("id-ID")}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setCashAmount(remainingToPay.toLocaleString("id-ID"))}
              className={`w-full py-2 border rounded-lg font-bold text-sm transition-colors ${
                cashValue === remainingToPay
                  ? "bg-teal-600 text-white border-teal-600"
                  : "bg-white border-teal-500 text-teal-600 hover:bg-teal-50"
              }`}>
              Uang Pas
            </button>
          </div>
        )}

        {isMixed && (
          <div className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-2.5">
            <p className="text-xs text-teal-700">Porsi Non-Tunai Otomatis</p>
            <p className="text-sm font-bold text-teal-800">
              Rp {mixedElectronicAmount.toLocaleString("id-ID")}
            </p>
          </div>
        )}

        {isMixed && cashValue > grandTotal && (
          <p className="text-xs text-red-600">
            Nominal cash tidak boleh melebihi grand total.
          </p>
        )}
      </div>

      {requiresAccount && (
        <div className="mt-4">
          <label className="text-sm font-semibold text-gray-700 block mb-1.5">
            Rekening Tujuan
          </label>
          <SearchableSelect
            options={paymentAccountOptions}
            value={accountId}
            onChange={setAccountId}
            fetchOptions={async (term) => {
              const response = await accountService.list({
                page: 1,
                limit: 100,
                query: term,
              });
              const rows = response?.data || [];
              return rows
                .filter((row) => {
                  if (row.status !== "ACTIVE" || row.type !== "PAYMENT_ACCOUNT") return false;
                  return isAccountSupportedForMethod(row, paymentMethod);
                })
                .map((account) => ({ id: account.id, name: account.name }));
            }}
            minSearchLength={1}
            placeholder={
              loadingAccounts ? "Memuat rekening..." : "Pilih rekening tujuan"
            }
            displayKey="name"
            valueKey="id"
            searchable
            disabled={loadingAccounts}
            className="w-full"
          />
        </div>
      )}

      {isNonCash && (
        <>
          <div className="mt-4">
            <label className="text-sm font-semibold text-gray-700 block mb-2">
              Bukti Pembayaran (Opsional)
            </label>
            <label className="block border-2 border-dashed border-gray-300 rounded-xl p-4 hover:border-teal-400 hover:bg-teal-50 transition cursor-pointer">
              <input
                type="file"
                onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                accept="image/*,.pdf"
                className="hidden"
              />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    Upload bukti pembayaran
                  </p>
                  <p className="text-xs text-gray-500">
                    PNG, JPG, JPEG, PDF. Bisa dilewati.
                  </p>
                </div>
              </div>
            </label>
            {proofFile && (
              <div className="mt-2 flex items-center justify-between rounded-lg border border-teal-200 bg-teal-50 px-3 py-2">
                <p className="text-xs font-medium text-teal-700 truncate pr-3">
                  {proofFile.name}
                </p>
                <button
                  type="button"
                  onClick={() => setProofFile(null)}
                  className="text-xs text-red-600 hover:text-red-700 font-semibold">
                  Hapus
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {isCash && cashAmount !== "" && !isDownPayment && (
        <div
          className={`mt-4 p-4 rounded-lg flex items-center gap-3 ${change >= 0 ? "bg-amber-50 border border-amber-200" : "bg-red-50 border border-red-200"}`}>
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${change >= 0 ? "bg-amber-100" : "bg-red-100"}`}>
            <Banknote
              className={`w-5 h-5 ${change >= 0 ? "text-amber-600" : "text-red-500"}`}
            />
          </div>
          <div>
            <p
              className={`text-xs font-medium ${change >= 0 ? "text-amber-700" : "text-red-600"}`}>
              {change >= 0 ? "Uang Kembalian" : "Kurang Bayar"}
            </p>
            <p
              className={`font-bold text-xl leading-tight ${change >= 0 ? "text-amber-600" : "text-red-600"}`}>
              Rp {Math.abs(change).toLocaleString("id-ID")}
            </p>
          </div>
        </div>
      )}



      </div>

      <div className="mt-4 pt-4 border-t border-gray-200 hidden lg:block">
        <button
          onClick={() =>
            onProcess({
              paymentMethod,
              cashAmount: isCash ? cashValue : isMixed ? cashValue : 0,
              electronicAmount: isCash
                ? 0
                : isMixed
                  ? mixedElectronicAmount
                  : isDownPayment
                    ? cashValue
                    : grandTotal,
              totalPaid,
              accountId: requiresAccount ? accountId : null,
              proofFile,
              isDownPayment,
            })
          }
          disabled={
            isProcessing ||
            !isSufficient ||
            !hasRequiredAmount ||
            (requiresAccount && !accountId)
          }
          className={`w-full py-4 rounded-xl font-bold text-base transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed ${isDownPayment
              ? "bg-amber-500 text-white hover:bg-amber-600"
              : "bg-black text-white hover:bg-gray-800"
            }`}>
          {isProcessing
            ? "Memproses..."
            : isDownPayment
              ? "Simpan DP & Tandai Pending"
              : "Proses Pembayaran"}
        </button>
      </div>
    </div>
  );
});

export default function EnhancedPaymentModal({
  cart,
  customer,
  walkInName = "",
  sellerType = { id: null, slug: "INTERNAL", name: "Internal" },
  salesTypes = [],
  selectedTable = null,
  selectedUnit = null,
  onClose,
  onSuccess,
  onOrderCreated,
  pendingOrder = null, // When set: pay existing PENDING order instead of creating new
  additionalCosts = 0,
  discountTotal = 0,
  notes = "",
}) {
  const paymentMethodRef = React.useRef(null);
  const isSubmittingRef = React.useRef(false);
  const isCompletedRef = React.useRef(false);
  const savedCartSnapshotRef = React.useRef(Array.isArray(cart) && cart.length > 0 ? cart : []);

  useEffect(() => {
    if (Array.isArray(cart) && cart.length > 0) {
      savedCartSnapshotRef.current = cart;
    }
  }, [cart]);

  const isApps = localStorage.getItem("PREVIEW_APPS") === "true";
  const { showSuccess, showError } = useNotification();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [itemAdjustments, setItemAdjustments] = useState({});
  const [billingModalField, setBillingModalField] = useState(null); // 'discount' | 'additionalCosts' | null
  const initialSubtotal = cart.reduce((sum, item) => sum + calculateOrderItemTotals(item).subtotal, 0);
  const [isManualDiscount, setIsManualDiscount] = useState(false);
  const [isManualMarkup, setIsManualMarkup] = useState(false);
  const [billing, setBilling] = useState({
    additionalCosts: additionalCosts || 0,
    discountTotal: discountTotal || 0,
    discountPercentage: discountTotal > 0 && initialSubtotal > 0 ? Math.round((discountTotal / initialSubtotal) * 100) : 0,
    notes: notes || "",
  });

  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [isDownPayment, setIsDownPayment] = useState(false);

  useEffect(() => {
    if (paymentMethod === "MIXED") {
      setIsDownPayment(false);
    }
  }, [paymentMethod]);

  const [pendingOrderDetail, setPendingOrderDetail] = useState(null);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [loadingPaymentAccounts, setLoadingPaymentAccounts] = useState(false);
  const [storeInfo, setStoreInfo] = useState({
    name: localStorage.getItem("tenantName") || "Toko",
    address: "",
    phone: "",
    logoUrl: "",
    footerNote: "",
  });

  const compressImage = (file, maxWidth = 800, quality = 0.7) =>
    new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const resolveSalesTypeMeta = (order) => {
    const pickFromSalesTypes = (predicate) => {
      const found = salesTypes.find(predicate);
      if (!found) return null;
      const slug = String(found?.slug || "").toUpperCase();
      return {
        id: found?.id,
        slug: slug || "",
        name: found?.name || found?.slug || "",
        jenis: found?.jenis || "",
        satuan: found?.satuan || "",
        nilai: found?.nilai || 0,
      };
    };

    if (order?.order_type && typeof order.order_type === "object") {
      return {
        id: order.order_type.id,
        slug: order.order_type.slug || order.order_type.name,
        name: order.order_type.name,
        jenis: order.order_type.jenis,
        satuan: order.order_type.satuan,
        nilai: order.order_type.nilai,
      };
    }

    const orderTypeId = order?.order_type_id;
    if (orderTypeId) {
      const byId = pickFromSalesTypes(
        (t) => String(t?.id || "") === String(orderTypeId),
      );
      if (byId?.name) return byId;
    }

    const rawSellerType = order?.seller_type;
    if (rawSellerType) {
      const sellerText = String(rawSellerType || "").trim();
      const sellerUpper = sellerText.toUpperCase();
      const bySlug = pickFromSalesTypes(
        (t) => String(t?.slug || "").toUpperCase() === sellerUpper,
      );
      if (bySlug?.name) return bySlug;
      const byName = pickFromSalesTypes(
        (t) => String(t?.name || "").toLowerCase() === sellerText.toLowerCase(),
      );
      if (byName?.name) return byName;
      return { slug: sellerUpper, name: sellerText };
    }

    if (sellerType && typeof sellerType === "object") {
      if (sellerType.id) {
        const byId = pickFromSalesTypes(
          (t) => String(t?.id || "") === String(sellerType.id),
        );
        if (byId?.name) return byId;
      }

      const slug = String(sellerType.slug || "").toUpperCase();
      if (slug) {
        const bySlug = pickFromSalesTypes(
          (t) => String(t?.slug || "").toUpperCase() === slug,
        );
        if (bySlug?.name) return bySlug;
        return { slug, name: sellerType.name || sellerType.slug };
      }

      if (sellerType.name) return { slug: "", name: sellerType.name };
    }

    const sellerText = typeof sellerType === "string" ? sellerType.trim() : "";
    if (sellerText) {
      const sellerUpper = sellerText.toUpperCase();
      const bySlug = pickFromSalesTypes(
        (t) => String(t?.slug || "").toUpperCase() === sellerUpper,
      );
      if (bySlug?.name) return bySlug;
      const byName = pickFromSalesTypes(
        (t) => String(t?.name || "").toLowerCase() === sellerText.toLowerCase(),
      );
      if (byName?.name) return byName;
      return { slug: sellerUpper, name: sellerText };
    }

    return { slug: "INTERNAL", name: "Internal" };
  };

  useEffect(() => {
    const loadPaymentAccounts = async () => {
      try {
        setLoadingPaymentAccounts(true);
        const response = await accountService.list({ page: 1, limit: 100 });
        const rows = response?.data || [];
        setPaymentAccounts(
          rows.filter(
            (row) => row.status === "ACTIVE" && row.type === "PAYMENT_ACCOUNT",
          ),
        );
      } catch (err) {
        console.error("Failed to load payment accounts:", err);
        showError("Gagal memuat akun pembayaran");
      } finally {
        setLoadingPaymentAccounts(false);
      }
    };

    loadPaymentAccounts();
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadStoreInfo = async () => {
      try {
        const response = await configService.getStore();
        const store = response?.data ?? response ?? {};
        if (!mounted) return;
        setStoreInfo({
          name: store?.name || localStorage.getItem("tenantName") || "Toko",
          address: store?.settings?.address || store?.address || "",
          phone: store?.settings?.phone || store?.phone || "",
          logoUrl: store?.logo_url || "",
          footerNote: store?.settings?.footer_note || "",
        });
      } catch (err) {
        if (!mounted) return;
        setStoreInfo((prev) => ({
          ...prev,
          name: localStorage.getItem("tenantName") || prev.name || "Toko",
        }));
      }
    };

    loadStoreInfo();
    return () => {
      mounted = false;
    };
  }, []);

  // Fetch full order detail (with items) when a pending order is set
  useEffect(() => {
    if (!pendingOrder?.id) {
      setPendingOrderDetail(null);
      return;
    }
    getSalesOrderDetail(pendingOrder.id)
      .then((res) => {
        const detail = res?.data ?? res;
        setPendingOrderDetail(detail);
        // Seed billing with server values so initial totals are correct
        setBilling((prev) => ({
          ...prev,
          additionalCosts: detail.additional_costs ?? 0,
          discountTotal: detail.discount_total ?? 0,
          discountPercentage: detail.discount_percentage ?? 0,
          notes: detail.notes || "",
        }));
      })
      .catch(() => { }); // silently fall back to list data
  }, [pendingOrder?.id]);

  const resolvedPendingOrder = pendingOrderDetail || pendingOrder;

  const fallbackPendingCart = (resolvedPendingOrder?.items || []).map(
    (item) => ({
      item_name:
        item.product_name ||
        item.service_name ||
        item.item_name ||
        item.name ||
        "-",
      quantity: item.quantity,
      unit_price: item.unit_price,
      discount_amount: Number(item.discount_amount || 0),
      additional_cost: Number(item.additional_cost || 0),
      notes: item.notes || "",
    }),
  );
  const baseActiveCart =
    pendingOrder && (!Array.isArray(cart) || cart.length === 0)
      ? fallbackPendingCart
      : (Array.isArray(cart) && cart.length > 0 ? cart : savedCartSnapshotRef.current);
  const activeCart = baseActiveCart.map((item, index) => ({
    ...item,
    ...(itemAdjustments[index] || {}),
  }));

  const handleUpdateItemAdjustment = (itemIndex, adjustment = {}) => {
    setItemAdjustments((previous) => ({
      ...previous,
      [itemIndex]: {
        discount_amount: Math.max(0, Number(adjustment.discount_amount || 0)),
        additional_cost: Math.max(0, Number(adjustment.additional_cost || 0)),
      },
    }));
  };

  // Normalize cart items to the shape calculateOrderTotals expects
  const normalizedCart = activeCart.map((item) => ({
    ...item,
    quantity: item.quantity || item.qty || 1,
    unit_price: item.unit_price || item.price || 0,
  }));

  const computedTotals = calculateOrderTotals(
    normalizedCart,
    billing.additionalCosts,
    billing.discountTotal,
  );

  const totalPaidSoFar = resolvedPendingOrder?.total_paid || 0;

  const totals = { ...computedTotals, dpAmount: totalPaidSoFar };

  const handleUpdateBilling = (field) => {
    setBillingModalField(field);
  };

  const handleSaveBilling = ({ nominal, percentage }) => {
    if (billingModalField === "discount") {
      setIsManualDiscount(true);
      setBilling((prev) => ({
        ...prev,
        discountTotal: nominal,
        discountPercentage: percentage,
      }));
    } else if (billingModalField === "additionalCosts") {
      setIsManualMarkup(true);
      setBilling((prev) => ({ ...prev, additionalCosts: nominal }));
    }
  };

  const handleFullTransaction = async (paymentData) => {
    if (isSubmittingRef.current || isProcessing || isSuccess || isCompletedRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    setIsProcessing(true);
    try {
      const sanitizeUUID = (val) => {
        if (!val || typeof val !== "string") return null;
        const trimmed = val.trim();
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return uuidRegex.test(trimmed) ? trimmed : null;
      };

      const roundInt = (val) => {
        const num = Number(val);
        return Number.isFinite(num) ? Math.round(num) : 0;
      };

      if (paymentData.paymentMethod !== "CASH" && !paymentData.accountId) {
        showError("Rekening tujuan wajib dipilih");
        return;
      }

      let proofPayload = {};
      if (paymentData.proofFile) {
        const proofBase64 = await compressImage(paymentData.proofFile);
        proofPayload = {
          payment_proof_base64: proofBase64,
          payment_proof_filename: paymentData.proofFile.name,
          payment_proof_content_type:
            paymentData.proofFile.type || "image/jpeg",
        };
      }

      if (pendingOrder) {
        // ── PAY EXISTING PENDING ORDER ──
        let orderTypeId = sellerType?.id || resolvedPendingOrder?.order_type_id;
        if (!orderTypeId && salesTypes.length > 0) {
          const typeSlug = (
            sellerType?.slug ||
            sellerType ||
            resolvedPendingOrder?.seller_type ||
            "INTERNAL"
          ).toUpperCase();
          const foundType = salesTypes.find(
            (t) => t.slug?.toUpperCase() === typeSlug,
          );
          orderTypeId = foundType?.id;
        }

        const orderUpdateData = {
          customer_id: sanitizeUUID(customer?.id),
          customer_name:
            customer?.name ||
            (walkInName && walkInName.trim()) ||
            resolvedPendingOrder?.customer_name ||
            "Pelanggan Umum",
          seller_type:
            sellerType?.name ||
            sellerType ||
            resolvedPendingOrder?.seller_type ||
            "Internal",
          order_type_id: sanitizeUUID(orderTypeId),
          items: activeCart.map((item) => {
            const isService = item.item_type === "SERVICE" || !!item.service_id;
            return {
              item_type: isService ? "SERVICE" : "PRODUCT",
              product_id: isService
                ? null
                : sanitizeUUID(item.product_id || (String(item.id || "").startsWith("cart-") || String(item.id || "").startsWith("pending-") ? null : item.id)),
              product_variant_id: sanitizeUUID(item.product_variant_id),
              product_addon_id: sanitizeUUID(item.product_addon_id),
              service_id: isService
                ? sanitizeUUID(item.service_id || (String(item.id || "").startsWith("cart-") || String(item.id || "").startsWith("pending-") ? null : item.product_id || item.id))
                : null,
              quantity: Number(item.quantity || item.qty || 1),
              unit_id: sanitizeUUID(item.unit?.id || item.asset_unit_id),
              ...getSellingUnitPayload(item),
              unit_name: item.unit?.name || item.asset_unit_name || (typeof item.unit === "string" ? item.unit : null) || null,
              unit_price: roundInt(item.unit_price || item.price || 0),
              discount_amount: roundInt(item.discount_amount),
              additional_cost: roundInt(item.additional_cost),
              notes: item.notes || "",
            };
          }),
          additional_costs: roundInt(billing.additionalCosts),
          discount_total: roundInt(billing.discountTotal),
          notes: billing.notes,
        };

        const expectedItems = orderUpdateData.items.map((item) => ({
          product_id: item.product_id || null,
          product_variant_id: item.product_variant_id || null,
          product_addon_id: item.product_addon_id || null,
          service_id: item.service_id || null,
          quantity: Number(item.quantity || 0),
        }));

        const hasExpectedOrderItems = (order) => {
          const actualItems = Array.isArray(order?.items) ? order.items : [];
          if (actualItems.length !== expectedItems.length) return false;

          const actualSignatures = actualItems
            .map((item) =>
              JSON.stringify({
                product_id: item?.product_id || null,
                product_variant_id: item?.product_variant_id || null,
                product_addon_id: item?.product_addon_id || null,
                service_id: item?.service_id || null,
                quantity: Number(item?.quantity || 0),
              }),
            )
            .sort();

          const expectedSignatures = expectedItems
            .map((item) => JSON.stringify(item))
            .sort();

          return actualSignatures.every(
            (signature, index) => signature === expectedSignatures[index],
          );
        };

        const updateResponse = await updateSalesOrder(
          pendingOrder.id,
          orderUpdateData,
        );

        let syncedOrder = updateResponse?.data ?? updateResponse ?? null;
        if (!hasExpectedOrderItems(syncedOrder)) {
          for (let attempt = 0; attempt < 4; attempt += 1) {
            await wait(350);
            const detailRes = await getSalesOrderDetail(pendingOrder.id);
            const latestOrder = detailRes?.data ?? detailRes ?? null;
            if (hasExpectedOrderItems(latestOrder)) {
              syncedOrder = latestOrder;
              break;
            }
            syncedOrder = latestOrder || syncedOrder;
          }
        }
        if (!hasExpectedOrderItems(syncedOrder)) {
          console.warn(
            "Order update verification is not exact yet, continuing payment with latest available order snapshot.",
            syncedOrder,
          );
        }

        const paymentPayload = {
          payment_method: paymentData.paymentMethod,
          ...(paymentData.paymentMethod !== "CASH" && paymentData.accountId
            ? { account_id: paymentData.accountId }
            : {}),
          cash_amount: paymentData.cashAmount || 0,
          electronic_amount: paymentData.electronicAmount || 0,
          ...proofPayload,
          notes: billing.notes || "",
        };
        await addOrderPayment(pendingOrder.id, paymentPayload);
        // Some backends mark the order as done automatically after payment.
        // If not supported, don't fail the whole payment flow after the payment is already saved.
        // For DP / partial payment, keep the order as PENDING so it stays in the
        // "Transaksi Pending" list until full settlement.
        if (!paymentData.isDownPayment) {
          try {
            await updateSalesOrderStatus(pendingOrder.id, { status: "DONE" });
          } catch (statusError) {
            console.warn(
              "Order payment saved, but status update failed:",
              statusError,
            );
          }
        }
        setCreatedOrder({
          ...syncedOrder,
          status: paymentData.isDownPayment ? "PENDING" : "DONE",
        });
        setPaymentSummary({
          method: paymentData.paymentMethod,
          amountPaid: paymentData.totalPaid,
          change: paymentData.isDownPayment
            ? 0
            : paymentData.totalPaid - totals.grandTotal,
          isDownPayment: !!paymentData.isDownPayment,
          remaining: paymentData.isDownPayment
            ? Math.max(0, totals.grandTotal - paymentData.totalPaid)
            : 0,
        });
        isCompletedRef.current = true;
        if (onOrderCreated) {
          try {
            onOrderCreated(syncedOrder || pendingOrder);
          } catch (callbackErr) {
            console.error("Error executing onOrderCreated callback:", callbackErr);
          }
        }

        setIsSuccess(true);
      } else {
        // ── CREATE NEW ORDER ──
        const payment = {
          payment_method: paymentData.paymentMethod,
          ...(paymentData.paymentMethod !== "CASH" && sanitizeUUID(paymentData.accountId)
            ? { account_id: sanitizeUUID(paymentData.accountId) }
            : {}),
          cash_amount: roundInt(paymentData.cashAmount),
          electronic_amount: roundInt(paymentData.electronicAmount),
          ...proofPayload,
          notes: billing.notes || "",
        };

        // Lookup order_type_id if not present
        let orderTypeId = sellerType?.id;
        if (!orderTypeId && salesTypes.length > 0) {
          const typeSlug = (
            sellerType?.slug ||
            sellerType ||
            "INTERNAL"
          ).toUpperCase();
          const foundType = salesTypes.find(
            (t) => t.slug?.toUpperCase() === typeSlug,
          );
          orderTypeId = foundType?.id;
        }

        const isDiscountFromTypeFlag = (sellerType?.jenis === "Diskon" || sellerType?.jenis === "Markup") && !isManualDiscount && !isManualMarkup;

        const orderData = {
          customer_id: sanitizeUUID(customer?.id),
          customer_name: customer?.name || (walkInName && walkInName.trim()) || "Pelanggan Umum",
          seller_type: sellerType?.name || sellerType || "Internal",
          order_type_id: sanitizeUUID(orderTypeId),
          items: activeCart.flatMap((item) => {
            const resultItems = [];
            
            const isService = item.item_type === "SERVICE" || !!item.service_id;

            // Parent item
            resultItems.push({
              item_type: isService ? "SERVICE" : "PRODUCT",
              product_id: isService
                ? null
                : sanitizeUUID(item.product_id || (String(item.id || "").startsWith("cart-") || String(item.id || "").startsWith("pending-") ? null : item.id)),
              product_variant_id: sanitizeUUID(item.product_variant_id),
              product_addon_id: sanitizeUUID(item.product_addon_id),
              service_id: isService
                ? sanitizeUUID(item.service_id || (String(item.id || "").startsWith("cart-") || String(item.id || "").startsWith("pending-") ? null : item.product_id || item.id))
                : null,
              quantity: Number(item.quantity || item.qty || 1),
              unit_id: sanitizeUUID(item.unit?.id || item.asset_unit_id),
              ...getSellingUnitPayload(item),
              unit_name: item.unit?.name || item.asset_unit_name || (typeof item.unit === "string" ? item.unit : null) || null,
              unit_price: roundInt(item.base_price || item.unit_price || item.price || 0),
              discount_amount: roundInt(item.discount_amount),
              additional_cost: roundInt(item.additional_cost),
              notes: item.notes || "",
            });

            // Options as separate items for backend inventory tracking
            if (item.selected_options && item.selected_options.length > 0) {
              item.selected_options.forEach((opt) => {
                resultItems.push({
                  item_type: "PRODUCT",
                  product_id: isService
                    ? null
                    : sanitizeUUID(item.product_id || (String(item.id || "").startsWith("cart-") || String(item.id || "").startsWith("pending-") ? null : item.id)),
                  product_addon_id: sanitizeUUID(opt.id),
                  quantity: Number(item.quantity || item.qty || 1) * Number(opt.qty || 1),
                  unit_id: sanitizeUUID(item.unit?.id || item.asset_unit_id), // Options usually share the parent unit context if needed
                  unit_name: item.unit?.name || item.asset_unit_name || (typeof item.unit === "string" ? item.unit : null) || null,
                  unit_price: roundInt(opt.adjusted_price || opt.price || opt.sell_price || 0),
                  notes: "",
                });
              });
            }

            return resultItems;
          }),
          additional_costs: roundInt(billing.additionalCosts),
          discount_total: roundInt(billing.discountTotal),
          discount_from_type: isDiscountFromTypeFlag,
          table_id: sanitizeUUID(selectedTable?.id || pendingOrder?.table_id),
          table_name: selectedTable?.name || pendingOrder?.table_name || null,
          payments: [payment],
          notes: billing.notes || "",
        };

        const response = await createSalesOrder(orderData);
        const newOrder = response?.data || response;
        isCompletedRef.current = true;

        if (onOrderCreated) {
          try {
            onOrderCreated(newOrder);
          } catch (callbackErr) {
            console.error("Error executing onOrderCreated callback:", callbackErr);
          }
        }

        // For DP, ensure the order remains PENDING so it shows up in
        // "Transaksi Pending" until pelunasan.
        if (paymentData.isDownPayment && newOrder?.id) {
          try {
            await updateSalesOrderStatus(newOrder.id, { status: "PENDING" });
          } catch (statusError) {
            console.warn(
              "DP saved, but explicit PENDING status update failed (backend may auto-handle):",
              statusError,
            );
          }
        }

        // Update Table Status
        const tableId = selectedTable?.id || pendingOrder?.table_id;
        if (tableId) {
          try {
            if (pendingOrder && !paymentData.isDownPayment) {
              // Open bill paid in full -> set table back to AVAILABLE
              await masterService.tables.updateStatus(tableId, { status: "AVAILABLE" });
            } else {
              // New order / DP -> mark table as OCCUPIED
              const customerLabel = customer?.name || (walkInName && walkInName.trim()) || "Pelanggan Umum";
              const statusTime = new Date().toLocaleTimeString("id-ID", {
                hour: "2-digit",
                minute: "2-digit",
              }).replace(".", ":");
              await masterService.tables.updateStatus(tableId, {
                status: "OCCUPIED",
                notes: `Hari ini, ${statusTime}`,
                reserved_by: customerLabel,
              });
            }
          } catch (tErr) {
            console.warn("Status meja gagal diperbarui:", tErr);
          }
        }

        setCreatedOrder({
          ...newOrder,
          discount_from_type: isDiscountFromTypeFlag,
          status: paymentData.isDownPayment
            ? "PENDING"
            : newOrder?.status || "DONE",
        });
        setPaymentSummary({
          method: paymentData.paymentMethod,
          amountPaid: paymentData.totalPaid,
          change: paymentData.isDownPayment
            ? 0
            : paymentData.totalPaid - totals.grandTotal,
          isDownPayment: !!paymentData.isDownPayment,
          remaining: paymentData.isDownPayment
            ? Math.max(0, totals.grandTotal - paymentData.totalPaid)
            : 0,
        });
        setIsSuccess(true);
      }
    } catch (err) {
      isSubmittingRef.current = false;
      console.error("Transaction failed:", err);
      showError(
        err.response?.data?.message ||
        err.message ||
        "Gagal memproses transaksi",
      );
    } finally {
      setIsProcessing(false);
      isSubmittingRef.current = false;
    }
  };

  const getTransactionReceiptData = () => {
    const txDateTime = createdOrder?.created_at
      ? new Date(createdOrder.created_at).toLocaleString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
      : new Date().toLocaleString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    const paidAt = new Date().toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

    const resolvedSalesType = resolveSalesTypeMeta(createdOrder);

    const receiptItems =
      createdOrder?.items && createdOrder.items.length > 0
        ? createdOrder.items
        : activeCart;

    const items = receiptItems.map((item) => {
      const name =
        item.name ||
        item.item_name ||
        item.product_name ||
        item.service_name ||
        "-";
      const qty = Number(item.qty || item.quantity || 0);
      const price = Number(item.price || item.unit_price || 0);
      const total = Number(item.total_price ?? qty * price);
      const rawUnit = item.unit_name || item.unit?.name || (typeof item.unit === "string" ? item.unit : "");
      const physicalSatuan =
        item.selling_unit_name ||
        item.satuan ||
        item.product_unit_name ||
        item.selling_unit_name ||
        item.selling_unit?.name ||
        (typeof item.selling_unit === "string" ? item.selling_unit : "") ||
        item.product?.unit ||
        item.product?.unit_name ||
        item.product?.selling_unit ||
        item.product?.selling_unit_name ||
        "";
      const isService = item.item_type === "SERVICE" || !!item.service_id;
      return {
        name,
        qty,
        price,
        total,
        discount_amount: Number(item.discount_amount || 0),
        additional_cost: Number(item.additional_cost || 0),
        unit: rawUnit || physicalSatuan || "",
        unit_name: rawUnit || "",
        asset_unit_name: item.asset_unit_name || "",
        satuan: physicalSatuan || "",
        product_id: isService ? null : (item.product_id || (String(item.id || "").startsWith("cart-") || String(item.id || "").startsWith("pending-") ? null : item.id)),
        service_id: isService ? (item.service_id || (String(item.id || "").startsWith("cart-") || String(item.id || "").startsWith("pending-") ? null : item.product_id || item.id)) : null,
        product_variant_id: item.variant_id || item.product_variant_id,
        product_addon_id: item.product_addon_id,
        selected_options: item.selected_options,
        variant_name: item.variant_name,
        notes: item.notes || item.note || "",
        item_type: isService ? "SERVICE" : "PRODUCT",
      };
    });

    const grandTotal = Number(createdOrder?.grand_total ?? totals.total ?? totals.grandTotal ?? 0);
    const subtotal = Number(createdOrder?.subtotal ?? totals.subtotal ?? grandTotal);
    const tunaiAmount = Number(paymentSummary?.amountPaid ?? grandTotal);
    const paymentMethodLabel =
      ({
        CASH: "Tunai",
        TRANSFER: "Transfer",
        QRIS: "QRIS",
        MIXED: "Campuran",
      })[paymentSummary?.method] || "Tunai";

    return {
      title: `Nota ${createdOrder?.transaction_number || "-"}`,
      storeName: storeInfo?.name || localStorage.getItem("tenantName") || "Toko",
      storeAddress: storeInfo?.address || "",
      storePhone: storeInfo?.phone || "",
      storeLogoUrl: storeInfo?.logoUrl || localStorage.getItem("tenantLogoUrl") || "",
      storeFooterNote: storeInfo?.footerNote || "Terima Kasih Telah Menjadi Pelanggan Kami",
      meta: {
        transactionNumber: createdOrder?.transaction_number || "-",
        transactionDateTime: txDateTime,
        queueNumber: createdOrder?.queue_number || createdOrder?.no_urut,
        cashierName: createdOrder?.created_by || createdOrder?.cashier_name || localStorage.getItem("activeShiftCashierName") || localStorage.getItem("username") || "-",
        customerName: customer?.name || (walkInName && walkInName.trim()) || createdOrder?.customer_name || "Pelanggan Umum",
        customerPhone: customer?.phone || customer?.phone_number || createdOrder?.customer_phone || "",
        customerAddress: customer?.address || createdOrder?.customer_address || "",
        unitName: selectedUnit?.name || selectedUnit?.unit_name || createdOrder?.unit_name || createdOrder?.asset_unit_name || detectVehicleUnit(createdOrder, items) || "",
        sellerType: resolvedSalesType?.name || "-",
        salesTypeObj: resolvedSalesType,
        tableName: selectedTable?.name || pendingOrder?.table_name || createdOrder?.table_name || "",
        transactionNotes: billing.notes || "",
      },
      items,
      totals: {
        subtotal,
        discount: createdOrder?.discount_total ?? billing?.discountTotal ?? 0,
        additionalCosts: createdOrder?.additional_costs ?? billing?.additionalCosts ?? 0,
        discount_from_type: !!(resolvedSalesType?.jenis === 'Diskon' || resolvedSalesType?.jenis === 'Markup') && !isManualDiscount && !isManualMarkup,
        total: grandTotal,
        dpAmount: totalPaidSoFar,
        tunai: tunaiAmount,
        totalBayar: grandTotal,
        paymentMethodLabel,
        change: paymentSummary?.change ?? 0,
        isDownPayment: !!paymentSummary?.isDownPayment,
        remaining: paymentSummary?.remaining ?? 0,
      },
      print: {
        paidAt,
        printedBy: createdOrder?.created_by || createdOrder?.cashier_name || "-",
      },
    };
  };

  const getOrderReceiptData = () => {
    const txDate = createdOrder?.created_at
      ? formatDate(createdOrder.created_at)
      : formatDate();

    const receiptItems =
      createdOrder?.items && createdOrder.items.length > 0
        ? createdOrder.items
        : activeCart;

    const items = receiptItems.map((item) => {
      const name =
        item.name ||
        item.item_name ||
        item.product_name ||
        item.service_name ||
        "-";
      const qty = Number(item.qty || item.quantity || 0);
      return {
        name,
        qty,
        category_id: item.category_id || item.category || null,
        product_id: item.product_id || item.id,
        product_variant_id: item.variant_id || item.product_variant_id,
        product_addon_id: item.product_addon_id,
        selected_options: item.selected_options,
        variant_name: item.variant_name,
        notes: item.notes || item.note || "",
        item_type: item.item_type || "PRODUCT",
      };
    });

    return {
      storeName: storeInfo?.name || localStorage.getItem("tenantName") || "Toko",
      storeFooterNote: storeInfo?.footerNote || "Dicetak untuk persiapan pesanan",
      meta: {
        transactionNumber: createdOrder?.transaction_number || "-",
        transactionDateTime: txDate,
        cashierName: createdOrder?.created_by || createdOrder?.cashier_name || localStorage.getItem("activeShiftCashierName") || localStorage.getItem("username") || "-",
        sellerType: resolveSalesTypeMeta(createdOrder).name,
        customerName: createdOrder?.customer_name || customer?.name || (walkInName && walkInName.trim()) || "-",
        unitName: selectedUnit?.name || selectedUnit?.unit_name || createdOrder?.unit_name || createdOrder?.asset_unit_name || detectVehicleUnit(createdOrder, items) || "",
        tableName: selectedTable?.name || pendingOrder?.table_name || createdOrder?.table_name || "",
        transactionNotes: billing.notes || "",
      },
      items,
    };
  };

  const handleOpenReceiptInNewTab = () => {
    const receiptData = getTransactionReceiptData();
    executePrint(receiptData, 'transaction')
      .then((res) => {
        if (res.method === 'bluetooth') {
           showSuccess("Struk berhasil dikirim ke printer bluetooth");
        }
      })
      .catch((err) => {
        showError(err?.message || "Printer belum terhubung. Silakan periksa Pengaturan Printer.");
      });
  };

  // ===== LABEL PESANAN =====
  /** Expand items berdasarkan qty: 3x Matcha = 3 label terpisah */
  const getLabelDataArray = () => {
    const txDate = createdOrder?.created_at ? new Date(createdOrder.created_at) : new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const datetime = `${pad(txDate.getDate())}/${pad(txDate.getMonth()+1)}/${String(txDate.getFullYear()).slice(2)} ${pad(txDate.getHours())}:${pad(txDate.getMinutes())}`;

    const storeName = storeInfo?.name || localStorage.getItem("tenantName") || "Toko";
    const customerName = createdOrder?.customer_name || customer?.name || (walkInName && walkInName.trim()) || "Pelanggan";
    const salesTypeName = resolveSalesTypeMeta(createdOrder)?.name || "";

    const printConfig = getReceiptConfig();
    const orderItems = createdOrder?.items || [];
    // Gabungkan category_id dari cart jika item createdOrder belum punya category_id
    const rawItems = orderItems
      .filter((it) => !it.product_addon_id)
      .map((it) => {
        const matchingCartItem = (cart && cart.length > 0 ? cart : savedCartSnapshotRef.current)?.find(
          (c) => (c.product_id || c.id) === (it.product_id || it.id)
        );
        return {
          ...it,
          category_id:
            it.category_id ||
            it.category ||
            matchingCartItem?.category_id ||
            matchingCartItem?.category ||
            null,
        };
      });

    // Filter berdasarkan kategori yang dipilih untuk label pesanan
    const filteredItems = filterLabelItems(rawItems, printConfig?.labelCategories);
    if (!filteredItems || filteredItems.length === 0) return [];

    // Expand berdasarkan qty: 2x item = 2 label
    const expanded = [];
    filteredItems.forEach((it) => {
      const qty = Number(it.quantity || it.qty || 1);
      const unit = it.unit || it.unit_name || "";
      const productName = it.item_name || it.product_name || it.name || "-";
      const variantName = it.variant_name || "";
      const notes = it.notes || it.note || it.catatan || it.item_note || it.product_note || it.description || "";
      // Kumpulkan addons milik item ini
      const addons = (createdOrder?.items || [])
        .filter((a) => a.product_addon_id && a.product_id === it.product_id)
        .map((a) => ({ name: a.item_name || a.name || "", qty: Number(a.quantity || a.qty || 1) }));
      for (let i = 0; i < qty; i++) {
        expanded.push({ storeName, customerName, salesTypeName, productName, variantName, qty: 1, unit, notes, addons, datetime });
      }
    });

    const total = expanded.length;
    return expanded.map((e, idx) => ({ ...e, index: idx + 1, total }));
  };

  const handlePrintLabels = () => {
    const labelArray = getLabelDataArray();
    if (!labelArray.length) {
      showError("Tidak ada produk dalam kategori label yang dipilih untuk dicetak");
      return;
    }
    executePrintLabel(labelArray)
      .then((res) => {
        if (res.method === "bluetooth") showSuccess("Label pesanan berhasil dikirim ke printer bluetooth");
      })
      .catch((err) => showError(err?.message || "Printer label belum terhubung. Silakan periksa Pengaturan Printer."));
  };

  const handleOpenOrderReceiptInNewTab = () => {
    const receiptData = getOrderReceiptData();
    executePrint(receiptData, 'order')
      .then((res) => {
        if (res.method === 'bluetooth') {
           showSuccess("Nota pesanan berhasil dikirim ke printer bluetooth");
        }
      })
      .catch((err) => {
        showError(err?.message || "Printer belum terhubung. Silakan periksa Pengaturan Printer.");
      });
  };

  const handleCompleteTransaction = () => {
    if (onSuccess) {
      onSuccess(createdOrder);
    }
    onClose();
  };

  const PAYMENT_METHOD_LABEL = {
    CASH: "Tunai",
    TRANSFER: "Transfer Bank",
    QRIS: "QRIS",
    MIXED: "Campuran",
  };

  const formatDate = (d) => {
    const date = d ? new Date(d) : new Date();
    return (
      date.toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }) +
      " " +
      date.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })
    );
  };

  // Auto Print trigger when transaction succeeds
  useEffect(() => {
    if (isSuccess && createdOrder) {
      const printConfig = getReceiptConfig();
      if (printConfig?.autoPrintEnabled) {
        if (printConfig?.orderReceiptEnabled) {
          const receiptDataTx = getTransactionReceiptData();
          const receiptDataOrd = getOrderReceiptData();
          executePrintBoth(receiptDataTx, receiptDataOrd)
            .then((res) => {
              if (res.method === 'bluetooth') {
                showSuccess("Nota transaksi & pesanan berhasil dikirim ke printer bluetooth");
              }
            })
            .catch((err) => {
              showError(err?.message || "Printer belum terhubung. Silakan periksa Pengaturan Printer.");
            });
        } else {
          const receiptDataTx = getTransactionReceiptData();
          executePrint(receiptDataTx, 'transaction')
            .then((res) => {
              if (res.method === 'bluetooth') {
                showSuccess("Struk berhasil dikirim ke printer bluetooth");
              }
            })
            .catch((err) => {
              showError(err?.message || "Printer belum terhubung. Silakan periksa Pengaturan Printer.");
            });
        }
      }
      // Auto Print Label Pesanan (Kuliner Only)
      if (printConfig?.orderLabelEnabled && printConfig?.autoPrintLabelEnabled) {
        const labelArray = getLabelDataArray();
        if (labelArray.length > 0) {
          executePrintLabel(labelArray)
            .then((res) => {
              if (res.method === 'bluetooth') showSuccess("Label pesanan dikirim ke printer bluetooth");
            })
            .catch((err) => showError(err?.message || "Printer label belum terhubung. Silakan periksa Pengaturan Printer."));
        }
      }
    }
  }, [isSuccess, createdOrder]);

  if (isSuccess) {
    const txDate = createdOrder?.created_at
      ? formatDate(createdOrder.created_at)
      : formatDate();
    const txNumber = createdOrder?.transaction_number || "-";
    const resolvedSalesType = resolveSalesTypeMeta(createdOrder) || { slug: "INTERNAL", name: "Internal" };
    const txTypeSlug = resolvedSalesType?.slug || "INTERNAL";
    const txTypeName = resolvedSalesType?.name || "Internal";
    const kasirName = createdOrder?.created_by || createdOrder?.cashier_name || "Kasir";
    const grandTotal = Number(createdOrder?.grand_total ?? totals?.total ?? totals?.grandTotal ?? 0);
    const amountPaid = Number(paymentSummary?.amountPaid ?? 0);
    const change = Number(paymentSummary?.change ?? 0);
    const methodLabel = PAYMENT_METHOD_LABEL[paymentSummary?.method] || "Tunai";
    const isDP = !!paymentSummary?.isDownPayment;
    const remaining = Number(paymentSummary?.remaining ?? 0);

    const handleSendWhatsapp = () => {
      const rawPhone = customer?.phone || customer?.phone_number || createdOrder?.customer_phone || "";
      const phone = rawPhone.replace(/[^\d+]/g, "");
      if (!phone) {
        showError("Nomor WhatsApp pelanggan tidak tersedia. Silakan catat nomor telepon pelanggan di data master.");
        return;
      }

      let formattedPhone = phone;
      if (formattedPhone.startsWith("0")) {
        formattedPhone = "62" + formattedPhone.slice(1);
      }

      const message = [
        `Halo ${customer?.name || (walkInName && walkInName.trim()) || createdOrder?.customer_name || "Pelanggan"}, berikut ringkasan transaksi Anda di *${storeInfo?.name || "Toko Kami"}*:`,
        ``,
        `No. Transaksi: *${txNumber}*`,
        `Tanggal: *${txDate}*`,
        `Total Belanja: *Rp ${grandTotal.toLocaleString("id-ID")}*`,
        ``,
        "Terima kasih telah berbelanja di tempat kami! 🙏"
      ].join("\n");

      const waWindow = window.open(`https://wa.me/${encodeURIComponent(formattedPhone)}?text=${encodeURIComponent(message)}`, "_blank");
      if (!waWindow) {
        showError("Popup tab baru diblokir browser");
      }
    };

    const InfoRow = ({
      icon: Icon,
      iconBg,
      label,
      value,
      valueClass = "font-semibold text-gray-800",
    }) => (
      <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
        <div className="flex items-center gap-2.5">
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center ${iconBg}`}>
            <Icon className="w-3.5 h-3.5" />
          </div>
          <span className="text-gray-500 text-xs font-medium">{label}</span>
        </div>
        <span className={`text-xs ${valueClass}`}>{value}</span>
      </div>
    );

    return (
      <div
        onClick={handleCompleteTransaction}
        className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 modal-backdrop-enter">
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-[390px] max-h-[92vh] overflow-y-auto modal-panel-enter relative">
          <button
            onClick={handleCompleteTransaction}
            className="absolute top-3.5 right-3.5 z-10 w-7 h-7 rounded-full bg-slate-100 text-slate-500 hover:text-slate-800 hover:bg-slate-200 flex items-center justify-center transition-colors"
            title="Tutup Modal">
            <X className="w-4 h-4" />
          </button>
          {/* Header */}
          <div className="p-5 pb-3 pr-10">
            <div className="mb-2">
              <h2 className="text-xl font-bold text-gray-900 leading-tight">
                {isDP ? "DP Tersimpan" : "Transaksi Berhasil"}
              </h2>
              <p className="text-xs text-gray-400 font-medium mt-0.5">{txDate}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-2">
              <span className="text-xs font-mono font-semibold text-gray-700">
                {txNumber}
              </span>
              <span
                className={`px-2 py-0.5 rounded text-[11px] font-bold ${txTypeSlug === "INTERNAL"
                    ? "bg-teal-100 text-teal-700"
                    : txTypeSlug === "EXTERNAL"
                      ? "bg-purple-100 text-purple-700"
                      : "bg-sky-100 text-sky-700"
                  }`}>
                {txTypeName}
              </span>
              {isDP && (
                <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-700">
                  Pending - DP
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">Kasir : {kasirName}</p>
          </div>

          {/* Dashed divider */}
          <div className="border-t-2 border-dashed border-gray-200 mx-4" />

          {/* Info rows */}
          <div className="px-4 py-2">
            <InfoRow
              icon={User}
              iconBg="bg-teal-50 text-teal-500"
              label="Nama Pelanggan"
              value={
                customer?.name ||
                (walkInName && walkInName.trim()) ||
                createdOrder?.customer_name ||
                "Pelanggan Umum"
              }
            />
            {localStorage.getItem("PREVIEW_APPS") === "true" && (selectedTable?.name || pendingOrder?.table_name || createdOrder?.table_name) && (
              <InfoRow
                icon={Utensils}
                iconBg="bg-teal-50 text-teal-600"
                label="Meja"
                value={selectedTable?.name || pendingOrder?.table_name || createdOrder?.table_name}
                valueClass="font-bold text-teal-700"
              />
            )}
            <InfoRow
              icon={ShoppingBag}
              iconBg="bg-teal-50 text-teal-500"
              label="Total Belanja"
              value={`Rp ${grandTotal.toLocaleString("id-ID")}`}
            />
            <InfoRow
              icon={CreditCard}
              iconBg="bg-teal-50 text-teal-500"
              label="Metode Pembayaran"
              value={methodLabel}
            />
            <InfoRow
              icon={Wallet}
              iconBg="bg-teal-50 text-teal-500"
              label="Jumlah Dibayar"
              value={`Rp ${amountPaid.toLocaleString("id-ID")}`}
            />
            {isDP ? (
              <InfoRow
                icon={Banknote}
                iconBg="bg-amber-50 text-amber-500"
                label="Sisa Tagihan"
                value={`Rp ${remaining.toLocaleString("id-ID")}`}
                valueClass="font-bold text-amber-600"
              />
            ) : (
              <InfoRow
                icon={Banknote}
                iconBg="bg-orange-50 text-orange-400"
                label="Uang Kembalian"
                value={`Rp ${change.toLocaleString("id-ID")}`}
                valueClass="font-bold text-orange-500"
              />
            )}
          </div>

          {/* Action buttons */}
          <div className="px-4 pb-4 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleSendWhatsapp}
                className="flex items-center justify-center gap-1.5 py-2.5 bg-teal-500 text-white rounded-xl text-xs font-semibold hover:bg-teal-600 transition-colors">
                <MessageCircle className="w-3.5 h-3.5" />
                Kirim ke Whatsapp
              </button>
              <button
                onClick={handleOpenReceiptInNewTab}
                className="flex items-center justify-center gap-1.5 py-2.5 bg-orange-500 text-white rounded-xl text-xs font-semibold hover:bg-orange-600 transition-colors">
                <Printer className="w-3.5 h-3.5" />
                Cetak Nota Transaksi
              </button>
            </div>
            {/* Baris Cetak Nota Pesanan & Label Pesanan (1 Line - Kuliner Only) */}
            {isApps && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleOpenOrderReceiptInNewTab}
                  className="w-full flex items-center justify-center gap-1.5 py-2.5 border-2 border-teal-500 text-teal-600 rounded-xl text-xs font-semibold hover:bg-teal-50 transition-colors">
                  <FileText className="w-3.5 h-3.5 text-teal-500" />
                  Cetak Nota Pesanan
                </button>
                <button
                  onClick={handlePrintLabels}
                  className="w-full flex items-center justify-center gap-1.5 py-2.5 border-2 border-emerald-500 text-emerald-700 rounded-xl text-xs font-semibold hover:bg-emerald-50 transition-colors">
                  <Tag className="w-3.5 h-3.5 text-emerald-600" />
                  Cetak Label Pesanan
                </button>
              </div>
            )}
            <button
              onClick={handleCompleteTransaction}
              className="w-full flex items-center justify-center gap-1.5 py-2.5 bg-gray-900 text-white rounded-xl text-xs font-semibold hover:bg-black transition-colors">
              <X className="w-3.5 h-3.5" />
              Tutup Transaksi
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black bg-opacity-60 flex flex-col justify-end lg:items-center lg:justify-center z-50 p-0 md:p-4 modal-backdrop-enter">
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full lg:max-w-7xl flex flex-col lg:grid lg:grid-cols-3 modal-panel-enter rounded-t-2xl lg:rounded-2xl shadow-2xl relative overflow-hidden"
          style={
            typeof window !== "undefined" && window.innerWidth >= 1024
              ? { minHeight: "85vh", maxHeight: "97vh" }
              : { height: "85vh", maxHeight: "85vh" }
          }>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-30 w-8 h-8 rounded-full bg-slate-100 text-slate-500 hover:text-slate-800 hover:bg-slate-200 flex items-center justify-center transition-colors shadow-sm"
            title="Tutup Modal">
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col lg:contents pb-24 lg:pb-0">
            <TransactionDetails
              cart={activeCart}
              onClose={onClose}
              onUpdateItemAdjustment={handleUpdateItemAdjustment}
            />
            <BillingSummary
              totals={totals}
              discountPercentage={billing.discountPercentage}
              onUpdateField={handleUpdateBilling}
              notes={billing.notes}
              onNotesChange={(e) =>
                setBilling((prev) => ({ ...prev, notes: e.target.value }))
              }
              isDownPayment={isDownPayment}
              setIsDownPayment={setIsDownPayment}
              paymentMethod={paymentMethod}
              sellerType={sellerType}
              isManualDiscount={isManualDiscount}
              isManualMarkup={isManualMarkup}
              customer={customer}
              walkInName={walkInName}
              selectedTable={selectedTable}
              pendingOrder={pendingOrder}
            />
            <PaymentMethod
              ref={paymentMethodRef}
              grandTotal={totals.grandTotal}
              totalPaidSoFar={totals.dpAmount}
              onProcess={handleFullTransaction}
              isProcessing={isProcessing}
              onClose={onClose}
              paymentAccounts={paymentAccounts}
              loadingAccounts={loadingPaymentAccounts}
              dpAmount={totals.dpAmount}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              isDownPayment={isDownPayment}
              setIsDownPayment={setIsDownPayment}
            />
          </div>

          {/* Mobile Sticky Footer */}
          <div className="lg:hidden absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 z-10 w-full flex gap-3 shadow-[0_-4px_10px_rgba(0,0,0,0.05)]">
             <button
               onClick={onClose}
               disabled={isProcessing}
               className="flex-1 py-3 bg-rose-100 text-rose-700 rounded-xl font-bold text-sm hover:bg-rose-200 transition-colors shadow-sm disabled:opacity-50">
               Kembali
             </button>
             <button
               onClick={() => paymentMethodRef.current?.submit()}
               disabled={isProcessing || isSuccess || isSubmittingRef.current || isCompletedRef.current}
               className="flex-[2] py-3 bg-teal-500 text-white rounded-xl font-bold text-sm hover:bg-teal-600 transition-colors shadow-lg shadow-teal-500/30 flex items-center justify-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed">
               {isProcessing ? "Memproses..." : "Proses Pembayaran"}
             </button>
          </div>
        </div>
      </div>

      {billingModalField && (
        <EditBillingModal
          field={billingModalField}
          initialNominal={
            billingModalField === "discount"
              ? billing.discountTotal
              : billing.additionalCosts
          }
          initialPercentage={
            billingModalField === "discount" ? billing.discountPercentage : 0
          }
          subtotal={totals.subtotal}
          onClose={() => setBillingModalField(null)}
          onSave={handleSaveBilling}
        />
      )}
    </>
  );
}
